import type { WywolanieWierszDto } from "@shared/wywolaniaTypy.js";
import type { KursDto } from "@shared/kosztyTypy.js";
import { Link } from "react-router-dom";
import {
  etykietaStatusu,
  formatujCzas,
  formatujMs,
  formatujTokenyWiersza,
  kosztWywolaniaDoPokazania,
  skrocId,
  sprzecznoscStatusu,
} from "../../lib/developer.js";
import { etykietaDostawcy, etykietaEtapu, formatujLiczbe } from "../../lib/koszty.js";
import { Badge } from "../ui/Badge.js";
import { Tooltip } from "../ui/Tooltip.js";

/**
 * Tabela wywolan (TB-1..TB-3, TB-8): czas, etap i model, status, zuzycie, czas odpowiedzi i koszt. Role ARIA stoja na
 * kazdym elemencie, bo na telefonie `display: block` zabieralby tabeli semantyke, a `data-etykieta` karmi mobilne
 * mini-karty (te same reguly CSS co tabele kosztow: `.tabela-danych`).
 *
 * Kwota to szacunek z cennika: brak zuzycia albo stawki to kreska z powodem w dymku (i w tekscie dla czytnika), a
 * nieudana proba bez zuzycia jest poza kosztem. Zero pojawia sie tylko wtedy, gdy koszt faktycznie policzono.
 */

function KomorkaKosztuWywolania({ w, kurs }: { w: WywolanieWierszDto; kurs: KursDto | null }): React.JSX.Element {
  const k = kosztWywolaniaDoPokazania(w.koszt, kurs);
  if (k.usd === null) {
    return (
      <Tooltip tekst={k.powod ?? ""}>
        <span className="koszt koszt--brak" tabIndex={0}>
          -<span className="tylko-dla-czytnika">{` (brak kosztu: ${k.powod})`}</span>
        </span>
      </Tooltip>
    );
  }
  const kwota = (
    <span className="koszt">
      <span className="koszt__usd">{k.usd}</span>
      {k.pln && <span className="koszt__pln">{k.pln}</span>}
      {k.powod !== null && <span className="tylko-dla-czytnika">{` (kwota jest dolnym ograniczeniem: ${k.powod})`}</span>}
    </span>
  );
  // Dolne ograniczenie ma dymek z powodem (z klawiatury tez: tabIndex), dokladna kwota nie potrzebuje wyjasnienia.
  if (k.powod === null) return kwota;
  return (
    <Tooltip tekst={k.powod}>
      <span tabIndex={0}>{kwota}</span>
    </Tooltip>
  );
}

export function TabelaWywolan({
  wiersze,
  kurs,
  powrotSearch = "",
}: {
  wiersze: readonly WywolanieWierszDto[];
  kurs: KursDto | null;
  /**
   * Query string listy (filtry i strona), do ktorego wraca link "Wroc do historii wywolan" na stronie szczegolu. Idzie w
   * `location.state` linku, wiec adres szczegolu zostaje czysty, a powrot nie gubi miejsca w liscie.
   */
  powrotSearch?: string;
}): React.JSX.Element {
  return (
    <div className="tabela-danych-wrap">
      <table className="tabela-danych tabela-wywolan" role="table" aria-label="Historia wywołań modeli">
        <thead role="rowgroup">
          <tr role="row">
            <th role="columnheader">Czas</th>
            <th role="columnheader">Etap i model</th>
            <th role="columnheader">Status</th>
            <th role="columnheader" className="is-num">
              Tokeny wejścia / wyjścia
            </th>
            <th role="columnheader" className="is-num">
              Czas odpowiedzi
            </th>
            <th role="columnheader" className="is-num">
              Koszt
            </th>
          </tr>
        </thead>
        <tbody role="rowgroup">
          {wiersze.map((w) => {
            const status = etykietaStatusu(w.status);
            // Wiersz "Udane" z zapisem, ktory temu przeczy (dane historyczne): mowimy to wprost zamiast zielonego "Udane".
            const sprzecznosc = sprzecznoscStatusu(w);
            return (
              <tr role="row" key={w.id}>
                <td role="cell" data-etykieta="Czas">
                  <Link
                    className="tabela-wywolan__link"
                    to={`/developer/${encodeURIComponent(w.id)}`}
                    state={{ powrotSearch }}
                    title={`Wywołanie ${w.id}`}
                  >
                    {formatujCzas(w.utworzono)}
                  </Link>
                  <span className="tabela-danych__podpis">{skrocId(w.id)}</span>
                </td>
                <td role="cell" data-etykieta="Etap i model">
                  {/* Jedno dziecko komorki: na telefonie komorka to flex, a dwa dzieci rozjechalyby sie w jednym rzedzie. */}
                  <span className="tabela-danych__wartosc">
                    <span className="tabela-danych__glowna">{etykietaEtapu(w.zakres)}</span>
                    <span className="tabela-danych__podpis">{`${etykietaDostawcy(w.dostawca)} - ${w.model}`}</span>
                  </span>
                </td>
                <td role="cell" data-etykieta="Status">
                  <span className="tabela-danych__wartosc">
                    <span>
                      <Badge wariant={status.wariant}>{status.etykieta}</Badge>
                      {sprzecznosc && (
                        <>
                          {" "}
                          <Badge wariant="warning">{sprzecznosc.etykieta}</Badge>
                        </>
                      )}
                    </span>
                    {sprzecznosc && <span className="tabela-danych__podpis">{sprzecznosc.opis}</span>}
                    {(w.httpStatus !== null || w.powodZakonczenia) && (
                      <span className="tabela-danych__podpis">
                        {[w.httpStatus !== null ? `HTTP ${w.httpStatus}` : null, w.powodZakonczenia].filter(Boolean).join(" - ")}
                      </span>
                    )}
                  </span>
                </td>
                <td role="cell" className="is-num" data-etykieta="Tokeny wejścia / wyjścia">
                  <span className="tabela-danych__wartosc">
                    <span>{formatujTokenyWiersza(w.tokenyWejscia, w.tokenyWyjscia, formatujLiczbe)}</span>
                    {(w.tokenyRozumowania !== null || w.tokenyZCache !== null) && (
                      <span className="tabela-danych__podpis">
                        {[
                          w.tokenyRozumowania !== null ? `rozumowanie ${formatujLiczbe(w.tokenyRozumowania)}` : null,
                          w.tokenyZCache !== null ? `z cache ${formatujLiczbe(w.tokenyZCache)}` : null,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    )}
                  </span>
                </td>
                <td role="cell" className="is-num" data-etykieta="Czas odpowiedzi">
                  <span className="tabela-danych__wartosc">
                    <span>{formatujMs(w.czasMs)}</span>
                  </span>
                </td>
                <td role="cell" className="is-num" data-etykieta="Koszt">
                  <KomorkaKosztuWywolania w={w} kurs={kurs} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
